import { fetchAllPositions } from '$lib/apis/userHoldings';
import { USER_BALANCE_REFRESH } from '$lib/refreshIntervals';
import type { Position } from '$lib/types/position';
import { pusherObject, subscribePusherChannel, unsubscribePusherChannel } from '$lib/ws/pusher';
import { get, writable, type Writable } from 'svelte/store';

const createPositionsRawStore = () => {
	const store: Writable<{
		data: Position[] | null;
		loading: boolean;
		error: any;
		pollingEnabled: boolean;
	}> = writable({
		data: null,
		loading: true,
		error: null,
		pollingEnabled: false
	});
	let scwAddress: `0x${string}`;
	let pollingInterval: ReturnType<typeof setInterval>;

	const apiCall = async ({ polling = false }) => {
		if (get(store).pollingEnabled) {
			try {
				const data = (await fetchAllPositions(scwAddress)) as Position[];
				store.update((oldStore) => {
					return { ...oldStore, data, error: null };
				});
			} catch (err) {
				store.update((oldStore) => {
					return { ...oldStore, error: err };
				});
				console.log(err);
			}
			if (polling) {
				pollingInterval = setInterval(() => apiCall({ polling: true }), USER_BALANCE_REFRESH);
			}
		}
		return;
	};

	const stopPolling = () => {
		if (pollingInterval) clearInterval(pollingInterval);
		store.update((oldStore) => {
			return { ...oldStore, pollingEnabled: false };
		});
	};

	const startPolling = () => {
		if (get(store).pollingEnabled) return;
		store.update((oldStore) => {
			return { ...oldStore, pollingEnabled: true };
		});
		apiCall({ polling: true });
	};

	const initialize = async (address: `0x${string}`) => {
		if (pusherObject) {
			try {
				// init store
				store.update((oldStore) => {
					return { ...oldStore, loading: true };
				});

				const data: Position[] = (await fetchAllPositions(address)) as Position[];

				scwAddress = address;

				store.update((oldStore) => {
					return { ...oldStore, data, loading: false, error: null };
				});

				const channel = subscribePusherChannel(`perps-v2-account-events-${address}`);

				pusherObject.connection.bind('disconnected', () => {
					startPolling();
				});

				pusherObject.connection.bind('connected', () => {
					stopPolling();
				});

				pusherObject.connection.bind('error', () => {
					// enable polling
					startPolling();
				});

				channel.bind('PositionOpened', (data: Position) => {
					store.update((oldStore) => {
						const oldPositions = oldStore.data as Position[];

						return { ...oldStore, data: [data, ...oldPositions], error: null };
					});
				});

				channel.bind('PositionModified', (data: Position) => {
					store.update((oldStore) => {
						const oldStoreData = oldStore.data as Position[];

						oldStoreData.forEach((position, i) => {
							if (position.marketIdentifier === data.marketIdentifier) {
								oldStoreData[i] = data;
								return;
							}
						});
						return { ...oldStore, data: oldStoreData, error: null };
					});
				});

				channel.bind('PositionClosed', (data: Position) => {
					store.update((oldStore) => {
						const oldStoreData = oldStore.data as Position[];

						const newStoreData = oldStoreData.filter(
							(position) => position.marketIdentifier !== data.marketIdentifier
						);

						return { ...oldStore, data: newStoreData, error: null };
					});
				});

				channel.bind('PositionLiquidated', (data: Position) => {});
			} catch (err) {
				store.update((oldStore) => {
					return { ...oldStore, data: null, error: err };
				});
				console.log(err);
			}
		}
	};

	const cleanUpStore = () => {};

	return {
		subscribe: store.subscribe,
		set: store.set,
		update: store.update,
		store,
		initialize,
		cleanUpStore,
		apiCall
	};
};

export const positionsRaw = createPositionsRawStore();
